import { useCallback, useContext } from 'react';
import { useRouteUtils } from '@src/hooks/use-route-utils.hook';
import { LoginRedirectContext } from '@src/contexts/login-redirect-context';
import WindowService from '@src/services/window.service';

export type FetchAsHook = (
  resource: RequestInfo | URL,
  options?: RequestInit | undefined,
  hookOptions?: {
    loginRedirect?: boolean;
  },
) => Promise<Response>;

/**
 * Provides a {@link fetch} function that renders the Login Redirect Modal when
 * the response is a 401.
 *
 * If your fetch request should not render the Login Redirect Modal, you can
 * disable this by setting `hookOptions.loginRedirect` to false.
 *
 * @example Disabling the Login Redirect functionality
 * ```
 * const fetch = useFetch();
 *
 * const response = await fetch({
 *   '/path/to/resource',
 *   {
 *     method: 'GET',
 *   },
 *   {
 *     loginRedirect: false,
 *   },
 * });
 * ```
 */
export const useFetch = () => {
  const showLoginRedirectModal = useContext(LoginRedirectContext);
  const { getInternalPath } = useRouteUtils();

  const wrappedFetch: FetchAsHook = useCallback(
    async (resource: string, options, hookOptions) => {
      const { loginRedirect = true } = hookOptions || {};

      const isInternalPath = resource.startsWith(WindowService.base + '/');

      // Respect the proxy base path for fetch calls to internal routes to ensure
      // we don't make calls against anything other than this particular deployment of
      // the app, if it's being hosted under a base path by a reverse proxy.
      const resourceToHit = isInternalPath
        ? getInternalPath(resource)
        : resource;

      const results = await fetch(resourceToHit, options);

      if (!results.ok && [401, 403].includes(results.status) && loginRedirect) {
        showLoginRedirectModal();
      }

      return results;
    },
    [showLoginRedirectModal],
  );

  return wrappedFetch;
};
